import {
  isPrefilledWeightSource,
  toCanonicalKg,
  type Dropset as DropsetT,
  type WeightSource,
  type WorkoutSet as WorkoutSetT,
} from '@jackedlogs/shared';
import { parseLocaleNumber } from './parseLocaleNumber';
import type { DraftSet } from './state/loggerDraftTypes';
import { draftDropsetRounds } from './dropsetDraft';

// The dropset builder and the two per-set field helpers, split out of
// workoutPayload.ts (SP-WORKOUTPAYLOAD, which was 454 lines). All three are
// pure DraftSet -> field projections with no React and no draft-wide state.
//
// `SetCommon` moved here with them rather than staying behind and being
// exported back. Both files use it, so either direction works — but keeping
// it here makes the dependency one-way for TYPES as well as values
// (workoutPayload -> buildDropsetSet), instead of a type-only cycle. The
// build-time result is the same; the import graph is easier to read.

// Per-set fields shared by every WorkoutSet kind, assembled once from the
// draft set's completion/notes/effort state.
export type SetCommon = {
  completedAt?: string;
  fail?: true;
  note?: string;
  rpe?: number;
  rir?: number;
};

// Carry the prefilled-weight provenance from the draft set onto the
// persisted weighted/bodyweight set (→ LogSet.weightSource) for the
// post-session deviation math. Data-only, no UI. Persist only when it
// carries signal: 'manual' (and absent) are equivalent by LogSet
// convention, so omit them — same omit-the-default rule as loadedSides=1.
export const weightSourceField = (s: DraftSet): { weightSource?: WeightSource } =>
  isPrefilledWeightSource(s.weightSource)
    ? { weightSource: s.weightSource }
    : {};

// Carry "this set went to the limit on the open variable" onto the
// persisted set (→ LogSet.amrap). Deliberately NOT in SetCommon: common
// is spread onto cardio + timed too, and those WorkoutSet variants don't
// declare `amrap` — an unknown key there fails the NewWorkout parse and
// would reject the whole save. The flag is wired for strength (weighted /
// bodyweight) only in this slice; when the hold/cardio forms are decided
// it's THIS field that extends, never a second one. Omit-the-default like
// weightSource: only `true` carries signal, absent reads as non-AMRAP, so
// legacy rows and plain sets stay byte-identical on the wire.
export const amrapField = (s: DraftSet): { amrap?: true } =>
  s.amrap === true ? { amrap: true } : {};

// Assemble a dropset/myorep set into its persisted WorkoutSet. The TOP
// round becomes the parent set (reps + weight/extra); the remaining
// rounds become the canonical Dropset chain. Which round is the top is
// resolved by draftDropsetRounds (shared with live totals), so an empty
// parent rep count never drops the set — the data lives in the chain.
// Returns null only when there is no usable top round (no reps, or a
// weighted top with no weight — an incomplete set the schema can't hold).
export const buildDropsetSet = (
  s: DraftSet,
  isBodyweight: boolean,
  common: SetCommon,
): WorkoutSetT | null => {
  const rounds = draftDropsetRounds(s);
  const top = rounds[0];
  if (!top) return null;
  const topReps = parseInt(top.reps, 10);
  if (!Number.isFinite(topReps) || topReps < 0) return null;
  const variant = s.dropset?.variant ?? 'classical';

  const steps: DropsetT['steps'] = [];
  for (let i = 1; i < rounds.length; i++) {
    const d = rounds[i]!;
    const r = parseInt(d.reps, 10);
    if (!Number.isFinite(r) || r <= 0) continue;
    const w = d.weight.trim() === '' ? null : parseLocaleNumber(d.weight);
    const stepCommon = d.completedAt ? { completedAt: d.completedAt } : {};
    if (isBodyweight) {
      if (w !== null && Number.isFinite(w)) {
        steps.push({ reps: r, extra: toCanonicalKg(w, s.unit), ...stepCommon });
      } else {
        steps.push({ reps: r, ...stepCommon });
      }
    } else if (w !== null && Number.isFinite(w) && w >= 0) {
      steps.push({ reps: r, weight: toCanonicalKg(w, s.unit), ...stepCommon });
    } else {
      steps.push({ reps: r, ...stepCommon });
    }
  }
  const dropset: DropsetT | undefined =
    steps.length > 0 ? { variant, steps } : undefined;

  if (isBodyweight) {
    const extraRaw = top.weight.trim();
    const extraAbs = extraRaw === '' ? null : parseLocaleNumber(extraRaw);
    const bwSides = { loadedSides: s.loadedSides };
    if (extraAbs !== null && Number.isFinite(extraAbs)) {
      const signed = top.extraNegative ? -Math.abs(extraAbs) : extraAbs;
      return {
        type: 'bodyweight',
        reps: topReps,
        extra: toCanonicalKg(signed, s.unit),
        ...bwSides,
        ...common,
        ...weightSourceField(s),
        ...amrapField(s),
        ...(dropset ? { dropset } : {}),
      };
    }
    return {
      type: 'bodyweight',
      reps: topReps,
      ...bwSides,
      ...common,
      ...weightSourceField(s),
      ...amrapField(s),
      ...(dropset ? { dropset } : {}),
    };
  }
  const weight = parseLocaleNumber(top.weight);
  if (!Number.isFinite(weight) || weight < 0) return null;
  return {
    type: 'weighted',
    reps: topReps,
    weight: toCanonicalKg(weight, s.unit),
    loadedSides: s.loadedSides,
    ...common,
    ...weightSourceField(s),
    ...amrapField(s),
    ...(dropset ? { dropset } : {}),
  };
};
